// Shared patient identifier validation and option lists.
//
// The create-patient flows (nav view + patient search modal) and the edit form
// all validate the same Medicare/DVA fields. This module is the single source
// of truth so those copies can't drift apart again.

export const SEX_OPTIONS = [
  { sex: "Male" },
  { sex: "Female" },
  { sex: "Intersex" },
];

export const DVA_STATUSES = [
  { status: "None", value: "DVA Number" },
  { status: "Gold", value: "DVA Gold" },
  { status: "White", value: "DVA White" },
  { status: "Orange", value: "DVA Orange" },
];

export const DVA_TYPES = ["DVA Number", "DVA Gold", "DVA White", "DVA Orange"];

// 9999 99999 9 / 9(IRN)
export function formatMedicareNumber(value) {
  if (!value) return value;
  const val = String(value).replace(/\D/g, "");
  let formatted = "";
  if (val.length > 0) formatted += val.substring(0, 4);
  if (val.length > 4) formatted += " " + val.substring(4, 9);
  if (val.length > 9) formatted += " " + val.substring(9, 10);
  if (val.length > 10) formatted += " " + val.substring(10, 11);
  return formatted;
}

export function validateMedicareNumber(number) {
  if (!number) return false;
  const clean = String(number).replace(/\D/g, "");
  if (clean.length < 10) return false;
  const weights = [1, 3, 7, 9, 1, 3, 7, 9];
  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += parseInt(clean[i]) * weights[i];
  }
  return sum % 10 === parseInt(clean[8]);
}

export function validateDVANumber(number) {
  if (!number) return true;
  const clean = String(number).replace(/\s/g, "").toUpperCase();
  // State Code (1), Optional War Code (0-3), Numbers (1-9), Optional Dependent Suffix (1)
  return /^[NVQWST][A-Z]{0,3}\d{1,9}[A-Z]?$/.test(clean);
}

// Cards expire at the end of their month, so anything in the current month or
// later is still valid.
export function validateExpiryInFuture(expiry) {
  if (!expiry) return true;
  const expiryDate = new Date(expiry);
  const now = new Date();
  now.setDate(1);
  now.setHours(0, 0, 0, 0);
  return expiryDate >= now;
}

// Built manually (not toLocaleDateString) so the output is always DD/MM/YYYY
// regardless of the runtime's locale settings, matching Kraken's
// Carbon::createFromFormat('d/m/Y', ...) parsing exactly.
export function formatExpiryForSubmit(date) {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// dob comes off a native date input as YYYY-MM-DD; Kraken wants DD/MM/YYYY.
export function formatDobForSubmit(dob) {
  if (!dob) return dob;
  const parts = String(dob).split("-");
  if (parts.length !== 3) return dob;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

// A patient's own facility wins over the logged-in location, so editing a
// patient in another facility uses that facility's rules.
export function isSimplifiedOnboarding(store, patient) {
  const patientFacility = patient?.physical_location?.location?.facility;
  if (patientFacility) {
    return !!patientFacility.simplified_patient_onboarding;
  }
  return !!store?.state?.currentLocation?.facility
    ?.simplified_patient_onboarding;
}

// TreeSelect gives back {"<uuid>": true}; everything downstream wants the uuid.
export function extractLocationUuid(selectedNodeKey) {
  if (!selectedNodeKey) return "";
  if (typeof selectedNodeKey === "string") return selectedNodeKey;
  return Object.keys(selectedNodeKey)[0] || "";
}

// Builds the /person/{uuid}/regulatory payload from the object-shaped form
// state, dropping empties and folding DVA status into the number.
export function buildRegulatoryPayload(regulatoryNumbers) {
  const payload = [];
  for (const key in regulatoryNumbers) {
    const item = regulatoryNumbers[key];
    if (
      !item ||
      !item.number ||
      !item.regulatory_type ||
      item.regulatory_type === "IHI Number"
    ) {
      continue;
    }
    const cleanNumber =
      typeof item.number === "string"
        ? item.number.split(" ").join("")
        : String(item.number);

    let submitData = cleanNumber;
    let submitType = item.regulatory_type;
    if (["DVA Gold", "DVA White", "DVA Orange"].includes(submitType)) {
      submitData = submitData + "|" + submitType;
      submitType = "DVA Number";
    }

    payload.push({
      data: submitData,
      regulatory_type: submitType,
      expiry: item.expiry ? formatExpiryForSubmit(item.expiry) : "",
    });
  }
  return payload;
}

export function emptyRegulatoryNumbers() {
  return {
    medicare_number: {
      number: "",
      regulatory_type: "Medicare Number",
      expiry: "",
    },
    dva_number: {
      number: "",
      regulatory_type: "DVA Number",
      expiry: "",
    },
  };
}

// The one definition of "can this patient be created".
//
// Medicare, DVA and IHI numbers are all optional — a patient can be created
// without any of them. Whatever *is* entered must still be valid, so
// half-typed identifiers can't be saved.
export function hasRequiredPatientDetails({
  personalInformation = {},
  regulatoryNumbers = {},
  hasLocation = false,
  isSimplified = false,
}) {
  const medicare = regulatoryNumbers.medicare_number || {};
  const dva = regulatoryNumbers.dva_number || {};

  const hasPersonalInfo = isSimplified
    ? !!(personalInformation.first_name && personalInformation.last_name)
    : !!(
        personalInformation.first_name &&
        personalInformation.last_name &&
        personalInformation.sex &&
        personalInformation.dob
      );

  const hasMedicare =
    validateMedicareNumber(medicare.number) &&
    !!medicare.expiry &&
    validateExpiryInFuture(medicare.expiry);

  // A Medicare number entered without a valid, unexpired expiry blocks the
  // save. The form shows a notice under the expiry field saying so.
  const medicareUsable = !medicare.number || hasMedicare;

  const dvaExpiryMissingNumber = !!dva.expiry && !dva.number;

  return (
    hasPersonalInfo &&
    medicareUsable &&
    !!hasLocation &&
    validateDVANumber(dva.number) &&
    validateExpiryInFuture(dva.expiry) &&
    !dvaExpiryMissingNumber
  );
}
